<script lang="tsx">
import { computed, defineComponent, toRef, type PropType } from 'vue'
import { createNamespace } from '@bd/vue3-utils'
import { Input, Table, type TableColumnType } from 'ant-design-vue'
import { FormItem, TableForm } from '-/Common/Form'
import { InputRmb } from '-/Common/Input'
import { getRequiredRule } from '@/constant/form'
import { genRewardNoResettleColumns, REWARD_NO_RESETTLE_DATA_SOURCE } from '../../tools'
import { calcMulAmount, sumDecimalBy, sumOtherItemsAmount } from '../../calc'
const [name] = createNamespace('GroundAttachCompEntryForm')
export default defineComponent({
  name,
  props: {
    dataSync: {
      type: Object as PropType<Partial<ApiJtZch.CompData>>
    },
    standard: {
      type: Object as PropType<Partial<ApiJtProj.CompStandardData>>
    },
    prefix: {
      type: Array as PropType<string[]>,
      default: () => []
    }
  },
  setup(props, { expose }) {
    /**
     * 生成表单字段的完整路径：prefix + 业务字段
     * 这里用数组形式，兼容 TableForm 的嵌套结构（例如 others[0].label / others[0].value）。
     */
    const genFormItemName = (key: (string | number)[]) => [...props.prefix, ...key]
    const data = toRef(props, 'dataSync', {})

    /**
     * 单行金额：数量 * 标准
     */
    const getRowTotal = (key: string) => calcMulAmount(data.value?.[key], props.standard?.[key])

    const totalAmount = computed(() => {
      // 1) 主表：按“数量 * 标准”累计
      const part1 = sumDecimalBy(REWARD_NO_RESETTLE_DATA_SOURCE, record => getRowTotal(record.key))
      // 2) 其他项：直接累加金额字段 value
      const part2 = sumOtherItemsAmount(data.value?.others)
      return part1.add(part2)
    })
    const mainColumns: TableColumnType[] = [
      ...genRewardNoResettleColumns(),
      {
        title: '标准(元/单位)',
        dataIndex: 'standard',
        width: 200,
        customRender({ record }) {
          return props.standard?.[record.key]
        }
      },
      {
        title: '数量',
        dataIndex: 'count',
        width: 150,
        customRender({ record }) {
          return (
            <FormItem name={genFormItemName([record.key])} rules={getRequiredRule(`${record.type}数量`)}>
              <InputRmb v-model:value={data.value![record.key]} style='width: 100%' />
            </FormItem>
          )
        }
      },
      {
        title: '金额(元)',
        dataIndex: 'total',
        width: 150,
        customRender({ record }) {
          return getRowTotal(record.key).toFixed(2)
        }
      }
    ]

    const renderMainTable = () => (
      <Table pagination={false} columns={mainColumns} dataSource={REWARD_NO_RESETTLE_DATA_SOURCE} size='small' class='ant-calc-table' />
    )

    const otherColumns: TableColumnType[] = [
      {
        title: '项目名称',
        dataIndex: 'group',
        width: 200,
        customRender() {
          return '其他'
        },
        customCell(_, rowIndex) {
          // “其他”作为同组表头：合并成一个单元格（rowSpan）
          if (rowIndex === 0) {
            const othersCount = data.value.others?.length || 0
            return {
              rowSpan: othersCount > 0 ? othersCount : 1
            }
          }
          return {
            rowSpan: 0
          }
        }
      },
      {
        title: '类别',
        dataIndex: 'label',
        minWidth: 200,
        customRender({ record, index }) {
          return (
            <FormItem name={genFormItemName(['others', index, 'label'])} rules={getRequiredRule(`其他-第 ${index + 1} 项 类别`)}>
              <Input v-model:value={record['label']} style='width: 100%' placeholder='请输入' allowClear />
            </FormItem>
          )
        }
      },
      {
        title: '金额(元)',
        dataIndex: 'value',
        width: 250,
        customRender({ record, index }) {
          return (
            <FormItem name={genFormItemName(['others', index, 'value'])} rules={getRequiredRule(`其他-第 ${index + 1} 项 金额`)}>
              <InputRmb v-model:value={record['value']} style='width: 100%' />
            </FormItem>
          )
        }
      }
    ]
    const renderOtherTable = () => (
      <div class='mt-24'>
        <TableForm columns={otherColumns} dataSync={data.value.others} />
      </div>
    )

    const renderAmount = () => {
      return <div class='amount-tip mt-24'>小计金额：{totalAmount.value.toFixed(2)} 元</div>
    }
    expose({
      totalAmount
    })
    return () => (
      <div>
        {renderMainTable()}
        {renderOtherTable()}
        {renderAmount()}
      </div>
    )
  }
})
</script>
<style lang="less" scoped>
.amount-tip {
  padding: @padding-md @padding-sm;

  color: @primary-color;
  font-weight: bold;
  background: @bg-fade-1;
}
</style>
